import { useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import ReactModal from 'react-modal';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import SearchBarTap from 'lib/Tracking/Srp/SearchBarTap';
import useEvents from 'lib/hooks/useEvents';
import useSrpMobileSidebar from 'lib/hooks/useSrpMobileSidebar';

import FiltersModal from 'components/Srp/FiltersModal';
import TextSearch from 'components/Shared/TextSearch';
import FilterPills from 'components/Srp/FilterPills/FilterPills';
import SortByModal from 'components/Srp/SortByModal';

import SrpEventContext from 'contexts/SrpEventContext';
import { colors } from 'components/styles/variables';
import { SearchFacetCounts } from 'lib/apis/SearchApi/types';
import useHashParamFeatureFlag from 'lib/hooks/useHashParamFeatureFlag';
import { useSearchBarModel } from '../useSearchbarModel';

import HeaderIconButton from '../HeaderIconButton';
import { modalStyles, modalStylesFullscreen, styles } from './styles';

export default function MobileSearchBar({
  totalCount,
  searchVehicles,
  isSeo = false,
  facetCounts,
}: {
  totalCount: number;
  searchVehicles: (sortBy?: string) => Promise<void>;
  isSeo?: boolean;
  facetCounts?: SearchFacetCounts;
}): JSX.Element {
  const { t } = useTranslation();
  const logger = useEvents();
  const srpEventContext = useContext(SrpEventContext);

  const { isOpen: shouldShowFiltersModal, toggle: toggleFiltersModal } = useSrpMobileSidebar();
  const [searchBarValue, handleSearchBarChange] = useSearchBarModel();
  const multiMakeModelEnabled = useHashParamFeatureFlag('multiMakeModel');
  const [shouldShowSortModal, toggleSortModal] = useReducer((shouldShow) => !shouldShow, false);
  const [shouldShowInputModal, toggleInputModal] = useReducer((shouldShow) => !shouldShow, false);
  const [inputText, setInputText] = useState<string>(searchBarValue?.searchText || '');

  useEffect(() => {
    setInputText(searchBarValue?.searchText || '');
  }, [searchBarValue]);

  const onClickSort = useCallback((): void => {
    toggleSortModal();
    window.scrollTo(0, 0);
  }, [toggleSortModal]);

  const placeholderText = useMemo(() => {
    if (multiMakeModelEnabled) {
      return t('srp.topBar.mmmSearchMobile');
    }
    return t('srp.topBar.searchMobile');
  }, [t, multiMakeModelEnabled]);

  const inputValue = useMemo(() => {
    if (multiMakeModelEnabled) {
      return undefined;
    }
    return inputText;
  }, [inputText, multiMakeModelEnabled]);

  return (
    <>
      <div className={classNames('mobile-text-search-container')}>
        <div className="search-input-wrapper">
          <div className="search-input-adornment">
            <div className="search-icon" />
          </div>
          <input
            className="search-input"
            onClick={toggleInputModal}
            placeholder={placeholderText}
            value={inputValue}
            readOnly
          />
        </div>
        <HeaderIconButton icon="sort" onClick={toggleSortModal} />
        <HeaderIconButton icon="filter" onClick={toggleFiltersModal} />
        <div className="mmt-modal-container">
          <ReactModal
            isOpen={shouldShowSortModal}
            onRequestClose={toggleSortModal}
            style={modalStyles}
          >
            <SortByModal onListItemClick={onClickSort} isSeo={isSeo} />
          </ReactModal>
        </div>
        <FiltersModal
          onClick={toggleFiltersModal}
          isModalOpen={shouldShowFiltersModal}
          totalCount={totalCount}
          searchVehicles={searchVehicles}
          facetCounts={facetCounts}
        />
      </div>
      <div className="filters-mobile">
        <FilterPills />
      </div>
      <ReactModal
        isOpen={shouldShowInputModal && !multiMakeModelEnabled}
        onRequestClose={toggleInputModal}
        style={modalStylesFullscreen}
      >
        <div className="modal-search-container">
          <TextSearch
            toggleModal={toggleInputModal}
            setInputText={setInputText}
            onClick={(): Promise<void> =>
              logger.log(SearchBarTap({ sourcePage: srpEventContext.sourcePage }))
            }
            onChange={handleSearchBarChange}
          />
        </div>
      </ReactModal>
      <style jsx>{styles}</style>
      <style jsx>{`
        // disables scrolling of main page in mobile
        :global(html) {
          position: ${shouldShowInputModal ? 'fixed' : undefined};
        }

        .search-input-wrapper {
          display: flex;
          flex-grow: 1;
          align-items: center;
          background-color: white;
          border-radius: 4px;
        }

        .search-input-adornment {
          display: flex;
          align-items: center;
        }

        .search-input {
          flex-grow: 1;
          border: none;
          outline: none;
          padding: 8px;
          font-family: inherit;
          font-style: normal;
          font-weight: 400;
          font-size: 16px;
          line-height: 24px;
          letter-spacing: 0.2px;
          background-color: transparent;
          cursor: pointer;
          color: ${colors.BLACK};
        }

        .search-input::placeholder {
          color: ${colors.G3};
          font-weight: 300;
        }
      `}</style>
    </>
  );
}
